import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { Row, Col, Button } from 'reactstrap';
import Sidebar from '../../Sidebar';
import { useNavigate } from 'react-router';
import { useCookies } from 'react-cookie';
import { displayError } from '../../helpers';
import ErrorToast from '../../ErrorToast';
import FloatingControls from '../../components/FloatingControls';
import FormPageLayout from '../../components/FormPageLayout';
import LoadingOverlay from '../../components/LoadingOverlay';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { getAllTax } from '../../services/TaxServices';
import { getAllVendors } from '../../services/VendorServices';
import {
    createPurchaseCreditNote,
    getGrnsForPurchaseNote,
    getPurchaseCreditNoteGrnContext,
    getPurchaseCreditNoteNumber,
} from '../../services/PurchaseCreditNoteServices';
import {
    applyPurchaseCreditNoteTypeToLines,
    buildPurchaseCreditNoteTotals,
    getPurchaseCreditNoteTypeLabel,
    PURCHASE_CREDIT_NOTE_TYPES,
    PURCHASE_NOTE_VOUCHER_MODES,
    recalculatePurchaseNoteLine,
    syncDocumentCreditAmounts,
} from './PurchaseCreditNoteUtils';
import TableRowsPurchaseCreditNote from './TableRowsPurchaseCreditNote';
import { getCurrentDate } from '../../utils/DateUtils';
import moment from 'moment';

function AccountingHintPanel({ hint }) {
    if (!hint) {
        return null;
    }
    return (
        <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
            <label className="form-group col-sm-2 text-right">Accounting</label>
            <div className="col-sm-10 small text-muted">
                <div>{hint.party_ledger} · {hint.expense_ledger} · {hint.gst_ledger}</div>
                <div>{hint.narration}</div>
            </div>
        </div>
    );
}

function PurchaseCreditNote() {
    const [cookies] = useCookies(['myToken']);
    const navigate = useNavigate();
    const [inputs, setInputs] = useState({
        cn_date: getCurrentDate(),
        cn_type: 'purchase_return',
        voucher_mode: 'Item Invoice',
    });
    const [plants, setPlants] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [grns, setGrns] = useState([]);
    const [taxes, setTaxes] = useState([]);
    const [taxMap, setTaxMap] = useState({});
    const [productLines, setProductLines] = useState([]);
    const [documentLines, setDocumentLines] = useState([]);
    const [accountingHint, setAccountingHint] = useState(null);
    const [inProgress, setInProgress] = useState(false);
    const [selectedVendorOption, setSelectedVendorOption] = useState(null);

    const totals = useMemo(
        () => buildPurchaseCreditNoteTotals(productLines, documentLines),
        [productLines, documentLines],
    );

    useEffect(() => {
        getUserAllowedPlants(cookies).then((response) => {
            setPlants((response.plant_list || []).filter((plant) => plant.status).map((plant) => ({
                value: plant.id,
                label: plant.plant_alias,
            })));
        });
        getAllVendors(cookies).then((response) => {
            setVendors((response.vendor_list || []).filter((vendor) => vendor.status).map((vendor) => ({
                value: vendor.id,
                label: vendor.entity_name,
            })));
        });
        getAllTax(cookies).then((response) => {
            const taxList = (response.tax_list || []).filter((tax) => tax.status);
            setTaxes(taxList.map((tax) => ({ value: tax.id, label: tax.name })));
            const map = {};
            taxList.forEach((tax) => {
                map[tax.id] = {
                    cgst_per: tax.tax_type === 'igst' ? 0 : tax.tax_value1,
                    sgst_per: tax.tax_type === 'igst' ? 0 : tax.tax_value2,
                    igst_per: tax.tax_type === 'igst' ? tax.tax_value1 : 0,
                };
            });
            setTaxMap(map);
        });
    }, [cookies]);

    useEffect(() => {
        if (!inputs.plant_id || !inputs.cn_date) {
            return;
        }
        getPurchaseCreditNoteNumber(cookies, inputs.plant_id, moment(inputs.cn_date).format('DD-MM-YYYY'))
            .then((response) => {
                setInputs((values) => ({
                    ...values,
                    prefix: response.prefix,
                    sl_no: response.cn_no,
                    cn_no: `${response.prefix}/${response.cn_no}`,
                }));
            })
            .catch(() => {});
    }, [cookies, inputs.plant_id, inputs.cn_date]);

    useEffect(() => {
        if (!inputs.plant_id || !inputs.vendor_id) {
            setGrns([]);
            return;
        }
        getGrnsForPurchaseNote(cookies, inputs.plant_id, inputs.vendor_id)
            .then((response) => {
                setGrns((response.grn_list || []).map((grn) => ({
                    value: grn.id,
                    label: `${grn.grn_no} (${grn.grn_date})`,
                })));
            })
            .catch(() => setGrns([]));
    }, [cookies, inputs.plant_id, inputs.vendor_id]);

    const loadGrnContext = useCallback((gmrId, cnType = inputs.cn_type) => {
        if (!gmrId) {
            setProductLines([]);
            setDocumentLines([]);
            setAccountingHint(null);
            return;
        }
        if (!Object.keys(taxMap).length) {
            return;
        }
        getPurchaseCreditNoteGrnContext(cookies, gmrId)
            .then((response) => {
                const nextProductLines = (response.product_lines || []).map((line, index) => (
                    recalculatePurchaseNoteLine({
                        ...line,
                        sl_no: index + 1,
                    }, taxMap[line.tax_id] || {})
                ));
                const typed = applyPurchaseCreditNoteTypeToLines(
                    cnType,
                    nextProductLines,
                    response.document_lines || [],
                );
                setProductLines(typed.productLines);
                setDocumentLines(typed.documentLines);
                setAccountingHint(response.accounting_hint || null);
            })
            .catch((error) => {
                displayError(error?.response?.data || { message: 'Failed to load GRN.' }, 'Load Failed');
            });
    }, [cookies, inputs.cn_type, taxMap]);

    useEffect(() => {
        loadGrnContext(inputs.gmr_id);
    }, [inputs.gmr_id, inputs.cn_type, loadGrnContext]);

    const onVendorSelect = (selected) => {
        setSelectedVendorOption(selected);
        setInputs((values) => ({
            ...values,
            vendor_id: selected ? selected.value : '',
            gmr_id: '',
        }));
        setProductLines([]);
        setDocumentLines([]);
        setAccountingHint(null);
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setInputs((values) => ({ ...values, [name]: value }));
        if (name === 'gmr_id') {
            loadGrnContext(value);
        }
        if (name === 'cn_type' && inputs.gmr_id) {
            const typed = applyPurchaseCreditNoteTypeToLines(value, productLines, documentLines);
            setProductLines(typed.productLines);
            setDocumentLines(typed.documentLines);
        }
    };

    const handleProductLineChange = (index, name, value) => {
        const rows = [...productLines];
        rows[index] = recalculatePurchaseNoteLine({
            ...rows[index],
            [name]: value,
        }, name === 'tax_id' ? (taxMap[value] || {}) : (taxMap[rows[index].tax_id] || {}), name);
        setProductLines(rows);
        setDocumentLines((lines) => syncDocumentCreditAmounts(inputs.cn_type, rows, lines));
    };

    const handleDocumentLineChange = (index, value) => {
        const rows = [...documentLines];
        rows[index] = { ...rows[index], credit_amt: value };
        setDocumentLines(rows);
    };

    const addProductRow = () => {
        setProductLines((rows) => {
            const nextRows = [...rows, recalculatePurchaseNoteLine({
                sl_no: rows.length + 1,
                description: '',
                unit: '',
                qty: 0,
                basic_rate: 0,
                taxable_amt: 0,
                tax_id: '',
            })];
            setDocumentLines((lines) => syncDocumentCreditAmounts(inputs.cn_type, nextRows, lines));
            return nextRows;
        });
    };

    const removeProductRow = (index) => {
        setProductLines((rows) => {
            const nextRows = rows.filter((_, rowIndex) => rowIndex !== index);
            setDocumentLines((lines) => syncDocumentCreditAmounts(inputs.cn_type, nextRows, lines));
            return nextRows;
        });
    };

    const buildPayload = () => ({
        plant_id: inputs.plant_id,
        vendor_id: inputs.vendor_id,
        gmr_id: inputs.gmr_id,
        cn_type: inputs.cn_type,
        voucher_mode: inputs.voucher_mode,
        cn_date: moment(inputs.cn_date).format('DD-MM-YYYY'),
        prefix: inputs.prefix,
        sl_no: inputs.sl_no,
        remarks: inputs.remarks || '',
        product_lines: productLines.map((line) => ({
            description: line.description,
            unit: line.unit,
            qty: line.qty,
            basic_rate: line.basic_rate,
            taxable_amt: line.taxable_amt,
            tax_id: line.tax_id,
        })),
        document_lines: documentLines.map((line) => ({
            gmr_id: line.gmr_id,
            credit_amt: line.credit_amt,
            remarks: line.remarks || '',
        })),
    });

    const handleSubmit = (event) => {
        event.preventDefault();
        Swal.fire({
            title: 'Do you want to save this purchase credit note?',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            setInProgress(true);
            createPurchaseCreditNote(cookies, buildPayload())
                .then((response) => {
                    Swal.fire({
                        title: 'Saved!',
                        text: response.message || 'Purchase credit note saved.',
                        icon: 'success',
                    }).then(() => navigate(`/PurchaseCreditNoteEdit/${response.id}/`));
                })
                .catch((error) => {
                    displayError(error?.response?.data || { message: error?.message }, 'Save Failed');
                })
                .finally(() => setInProgress(false));
        });
    };

    const Reset = () => {
        setInputs({
            cn_date: getCurrentDate(),
            cn_type: 'purchase_return',
            voucher_mode: 'Item Invoice',
        });
        setSelectedVendorOption(null);
        setProductLines([]);
        setDocumentLines([]);
        setGrns([]);
        setAccountingHint(null);
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <ErrorToast />
            <LoadingOverlay inProgress={inProgress} />
            <div id="page-wrap">
                <form onSubmit={handleSubmit}>
                    <FloatingControls tableLink="/PurchaseCreditNoteTable" onCancel={Reset} enableCancel={true} />
                    <FormPageLayout
                        moduleLabel="PURCHASE"
                        title="Create Purchase Credit Note"
                        subtitle="Record vendor credit against GRN for purchase return, rate difference, discount, or other adjustments."
                        modeLabel={`${getPurchaseCreditNoteTypeLabel(inputs.cn_type).toUpperCase()} CREATE MODE`}
                    >
                        <div className="container form-control-panel">
                            <Row>
                                <Col xl={6} lg={12} md={12}>
                                    <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                        <label htmlFor="plant_id" className="form-group col-sm-5 text-right">Plant Location</label>
                                        <select id="plant_id" name="plant_id" className="form-control col-sm-6 browser-default custom-select mandatory-form-control" required onChange={handleChange} value={inputs.plant_id || ''}>
                                            <option value="">Select Plant</option>
                                            {plants.map((item) => (
                                                <option key={item.value} value={item.value}>{item.label}</option>
                                            ))}
                                        </select><br />

                                        <label htmlFor="vendor_id" className="form-group col-sm-5 text-right">Vendor Name</label>
                                        <div className="col-sm-6" style={{ paddingLeft: 0, paddingRight: 0 }}>
                                            <Select
                                                id="vendor_id"
                                                inputId="vendor_id"
                                                options={vendors}
                                                isSearchable={true}
                                                value={selectedVendorOption}
                                                onChange={onVendorSelect}
                                                placeholder="Select Vendor"
                                                className="mandatory-form-control react-select-dropdown"
                                            />
                                            <input type="hidden" name="vendor_id" value={inputs.vendor_id || ''} required />
                                        </div><br />

                                        <label htmlFor="gmr_id" className="form-group col-sm-5 text-right">GRN No</label>
                                        <select id="gmr_id" name="gmr_id" className="form-control col-sm-6 browser-default custom-select mandatory-form-control" required onChange={handleChange} value={inputs.gmr_id || ''}>
                                            <option value="">Select GRN</option>
                                            {grns.map((item) => (
                                                <option key={item.value} value={item.value}>{item.label}</option>
                                            ))}
                                        </select><br />

                                        <label htmlFor="cn_type" className="form-group col-sm-5 text-right">Credit Note Type</label>
                                        <select id="cn_type" name="cn_type" className="form-control col-sm-6 browser-default custom-select mandatory-form-control" required onChange={handleChange} value={inputs.cn_type || ''}>
                                            {PURCHASE_CREDIT_NOTE_TYPES.map((item) => (
                                                <option key={item.value} value={item.value}>{item.label}</option>
                                            ))}
                                        </select><br />

                                        <label htmlFor="voucher_mode" className="form-group col-sm-5 text-right">Voucher Mode</label>
                                        <select id="voucher_mode" name="voucher_mode" className="form-control col-sm-6 browser-default custom-select mandatory-form-control" required onChange={handleChange} value={inputs.voucher_mode || ''}>
                                            {PURCHASE_NOTE_VOUCHER_MODES.map((item) => (
                                                <option key={item.value} value={item.value}>{item.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </Col>
                                <Col xl={6} lg={12} md={12}>
                                    <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                        <label className="form-group col-sm-4 text-right">Credit Note No.</label>
                                        <input type="text" className="form-control col-sm-6" value={inputs.cn_no || ''} readOnly style={{ backgroundColor: 'white', cursor: 'not-allowed' }} /><br />
                                        <label htmlFor="cn_date" className="form-group col-sm-4 text-right">Credit Note Date</label>
                                        <input type="date" id="cn_date" name="cn_date" className="form-control col-sm-6" required onChange={handleChange} value={inputs.cn_date || ''} />
                                    </div>
                                </Col>
                            </Row>

                            <div className="form-row shadow p-2 my-2 border-secondary p-2 mb-3 bg-blue container table-responsive">
                                <table className="table-bordered searchTable table order-list" style={{ textAlign: 'center' }}>
                                    <thead>
                                        <tr>
                                            <th>Action</th>
                                            <th>S.No</th>
                                            <th>Description</th>
                                            <th>Unit</th>
                                            <th>Qty</th>
                                            <th>Basic Rate</th>
                                            <th>Amount</th>
                                            <th>Tax %</th>
                                            <th>Tax Amount</th>
                                            <th>Total Amount</th>
                                        </tr>
                                    </thead>
                                    <TableRowsPurchaseCreditNote
                                        rowsData={productLines}
                                        taxes={taxes}
                                        cnType={inputs.cn_type}
                                        onChange={handleProductLineChange}
                                        onAddRow={addProductRow}
                                        onRemoveRow={removeProductRow}
                                    />
                                </table>
                                <table className="table order-list" style={{ textAlign: 'center', backgroundColor: 'white' }}>
                                    <tbody>
                                        <tr>
                                            <td colSpan="6" style={{ textAlign: 'right' }}><b>Total Taxable Amount</b></td>
                                            <td colSpan="4"><input type="text" className="form-control" readOnly value={totals.taxable_amt} /></td>
                                            <td colSpan="6" style={{ textAlign: 'right' }}><b>CGST Amount</b></td>
                                            <td colSpan="4"><input type="text" className="form-control" readOnly value={totals.cgst_amt} /></td>
                                        </tr>
                                        <tr>
                                            <td colSpan="6" style={{ textAlign: 'right' }}><b>Total GST Amount</b></td>
                                            <td colSpan="4"><input type="text" className="form-control" readOnly value={totals.gst_amt} /></td>
                                            <td colSpan="6" style={{ textAlign: 'right' }}><b>SGST Amount</b></td>
                                            <td colSpan="4"><input type="text" className="form-control" readOnly value={totals.sgst_amt} /></td>
                                        </tr>
                                        <tr>
                                            <td colSpan="6" style={{ textAlign: 'right' }}><b>Total Gross Amount</b></td>
                                            <td colSpan="4"><input type="text" className="form-control" readOnly value={totals.gross_amt} /></td>
                                            <td colSpan="6" style={{ textAlign: 'right' }}><b>IGST Amount</b></td>
                                            <td colSpan="4"><input type="text" className="form-control" readOnly value={totals.igst_amt} /></td>
                                        </tr>
                                        <tr>
                                            <td colSpan="6" style={{ textAlign: 'right' }}><b>Round off</b></td>
                                            <td colSpan="4"><input type="text" className="form-control" readOnly value={totals.round_off} /></td>
                                            <td colSpan="6" style={{ textAlign: 'right' }}><b>Total Credit Note Value</b></td>
                                            <td colSpan="4"><input type="text" className="form-control" readOnly value={totals.credit_amt} /></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div className="form-row shadow p-2 my-2 border-secondary p-2 mb-3 bg-blue container table-responsive">
                                <table className="table-bordered searchTable table order-list" style={{ textAlign: 'center' }}>
                                    <thead>
                                        <tr>
                                            <th>Sl. No.</th>
                                            <th>Doc Type</th>
                                            <th>Reference No</th>
                                            <th>Reference Date</th>
                                            <th>Reference Amount</th>
                                            <th>Balance Amount</th>
                                            <th>Credit Amount</th>
                                            <th>Remarks</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {documentLines.map((line, index) => (
                                            <tr key={`pcn-doc-${index}`}>
                                                <td>{index + 1}</td>
                                                <td>{line.doc_type || 'GRN'}</td>
                                                <td>{line.reference_no}</td>
                                                <td>{line.reference_date}</td>
                                                <td>{line.reference_amt}</td>
                                                <td>{line.balance_amt}</td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={line.credit_amt ?? ''}
                                                        onChange={(event) => handleDocumentLineChange(index, event.target.value)}
                                                    />
                                                </td>
                                                <td>{line.remarks || ''}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                <label htmlFor="remarks" className="form-group col-sm-2 text-right">Remarks</label>
                                <textarea rows="2" cols="50" className="form-control col-sm-10" id="remarks" name="remarks" onChange={handleChange} value={inputs.remarks || ''} />
                            </div>

                            <AccountingHintPanel hint={accountingHint} />
                        </div>

                        <div className="footer text-center">
                            <Button type="submit" className="btn btn-twitter" style={{ width: '80px', fontWeight: 'bold' }}>Save</Button>
                            <Button type="button" className="btn btn-twitter" style={{ width: '80px', fontWeight: 'bold' }} onClick={Reset}>Reset</Button>
                            <Button type="button" className="btn btn-twitter" style={{ width: '80px', fontWeight: 'bold' }} onClick={() => navigate('/PurchaseCreditNoteTable')}>View</Button>
                            <Button type="button" className="btn btn-twitter" style={{ width: '80px', fontWeight: 'bold' }} onClick={() => navigate('/Home')}>Home</Button>
                        </div>
                    </FormPageLayout>
                </form>
            </div>
        </div>
    );
}

export default PurchaseCreditNote;
